import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Map as MapGL, Marker as MapMarker, Source, Layer } from '@vis.gl/react-maplibre';
import { config as maplibreConfig } from 'maplibre-gl';
import { circle as turfCircle } from '@turf/turf';
import 'maplibre-gl/dist/maplibre-gl.css';
import { X, Navigation, MapPin, Moon, Sun } from 'lucide-react';
import { getDistance } from 'geolib';

const MAP_STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';
const MAP_STYLE_URL_NIGHT = 'https://tiles.openfreemap.org/styles/dark';

// Recursively swaps any string in a paint object that exactly matches a key
// in `colorMap` — walks into expression arrays (e.g. ["interpolate", ...])
// too, so zoom-interpolated colors get caught the same as flat hex strings.
const remapColors = (node, colorMap) => {
  if (Array.isArray(node)) return node.map(n => remapColors(n, colorMap));
  if (node && typeof node === 'object') {
    const out = {};
    for (const k in node) out[k] = remapColors(node[k], colorMap);
    return out;
  }
  return typeof node === 'string' && colorMap[node] ? colorMap[node] : node;
};

// OpenFreeMap's "liberty" style leans bright/cartoonish (saturated blue
// water, candy-orange highways, poster-green parks, pure-black labels).
// Desaturate and deepen the whole palette toward something sleeker/more
// editorial — same shapes and hierarchy, just toned down — without touching
// layer structure, filters, or zoom breakpoints.
const DAY_COLOR_MAP = {
  // base / land
  '#f8f4f0': '#f1ece4',
  // water
  'rgb(158,189,255)': 'rgb(163,182,201)',
  '#a0c8f0': '#8fa7c0',
  // parks / landcover
  '#d8e8c8': '#d4dcc5',
  'rgba(228, 241, 215, 1)': 'rgba(216, 223, 203, 1)',
  'hsla(98,61%,72%,0.7)': 'hsla(100,22%,60%,0.5)',
  'rgba(176, 213, 154, 1)': 'rgba(195, 205, 177, 1)',
  'rgba(224, 236, 236, 1)': 'rgba(227, 231, 229, 1)',
  '#DEE3CD': '#D7D8C9',
  'hsl(75,37%,81%)': 'hsl(75,14%,77%)',
  '#fde': '#e9dcdc',
  'rgb(236,238,204)': 'rgb(227,225,209)',
  'rgba(247, 239, 195, 1)': 'rgba(233,224,199,1)',
  'hsla(0,3%,85%,0.84)': 'hsla(0,3%,85%,0.7)',
  'hsla(35,57%,88%,0.49)': 'hsla(35,20%,85%,0.45)',
  // roads
  '#e9ac77': '#c5926b',
  '#fc8': '#d8a066',
  '#ffdaa6': '#d2ac7d',
  '#fff4c6': '#e7dbb6',
  '#fea': '#dfbf7f',
  'hsl(26,87%,62%)': 'hsl(26,42%,54%)',
  '#cfcdca': '#c8c4bd',
  // buildings
  'hsl(35,8%,85%)': 'hsl(30,10%,79%)',
  'hsla(35,6%,79%,0.32)': 'hsla(30,8%,73%,0.32)',
  'hsl(35,6%,79%)': 'hsl(30,8%,73%)',
  // labels
  '#000': '#2a2622',
  '#333': '#3a352f',
  '#666': '#5c564e',
  '#495e91': '#55698f',
  '#74aee9': '#7f9ab3',
  '#2e5a80': '#3b5873',
};

// Minor/service/track streets render at every zoom by default, which at a
// city-wide view turns into a dense tangle that reads as "more highways"
// even though they're a different color — hide them until the user is
// actually zoomed into a neighborhood.
const MINOR_STREET_MINZOOM = 15;
const DAY_MINOR_STREET_LAYER_IDS = ['road_minor', 'road_minor_casing', 'road_service_track', 'road_service_track_casing'];

const remixDayColors = (styleJson) => ({
  ...styleJson,
  layers: styleJson.layers.map(layer => {
    const next = layer.paint ? { ...layer, paint: remapColors(layer.paint, DAY_COLOR_MAP) } : layer;
    return DAY_MINOR_STREET_LAYER_IDS.includes(layer.id)
      ? { ...next, minzoom: MINOR_STREET_MINZOOM }
      : next;
  })
});

// OpenFreeMap's "dark" style renders every road class (motorway down to
// tertiary) in near-identical grays, so highways don't read as highways at
// night. Recolor just the highway layers to a dark-blue hierarchy —
// motorways brightest, trunk/primary a step down, secondary/tertiary the
// most muted — while leaving minor streets untouched. All map text labels
// (place names, road names, water names, ...) are pushed to white, since
// the style's default grays read as barely-there against the dark basemap.
const recolorNightHighways = (styleJson) => {
  const majorColorByClass = ['match', ['get', 'class'],
    'trunk', '#35588f',
    'primary', '#2a4877',
    /* secondary / tertiary */ '#1f3356'
  ];
  const paintOverrides = {
    highway_motorway_casing: { 'line-color': 'rgba(10,18,40,0.9)' },
    highway_motorway_inner: { 'line-color': '#2d4f8c' },
    highway_motorway_subtle: { 'line-color': '#2d4f8c' },
    highway_major_casing: { 'line-color': 'rgba(8,14,28,0.85)' },
    highway_major_inner: { 'line-color': majorColorByClass },
    highway_major_subtle: { 'line-color': majorColorByClass },
  };

  return {
    ...styleJson,
    layers: styleJson.layers.map(layer => {
      let paint = paintOverrides[layer.id] ? { ...layer.paint, ...paintOverrides[layer.id] } : layer.paint;
      if (layer.type === 'symbol' && paint && 'text-color' in paint) {
        paint = { ...paint, 'text-color': '#ffffff' };
      }
      let next = paint !== layer.paint ? { ...layer, paint } : layer;
      if (layer.id === 'highway_minor') {
        next = { ...next, minzoom: MINOR_STREET_MINZOOM };
      }
      return next;
    })
  };
};

// CRA's webpack config can't reliably auto-bundle maplibre-gl's self-loading
// `new Worker(new URL(...))` — it 404s/aborts under the dev server, which
// surfaces as "Worker failed to load" and leaves the map blank. Point it at
// the static copy in public/ instead (kept in sync by `npm run postinstall`
// -> scripts/copy-maplibre-worker.js).
maplibreConfig.WORKER_URL = `${process.env.PUBLIC_URL}/maplibre-gl-worker.mjs`;

// ============= HELPER FUNCTIONS =============

const clusterNearbyItems = (locationGroups, distanceThresholdMeters = 1609.34) => {
  const clusters = [];
  const processed = new Set();

  locationGroups.forEach((group, index) => {
    if (processed.has(index)) return;

    const cluster = {
      ...group,
      clusterSize: 1,
      allItems: [...group.items]
    };

    locationGroups.forEach((otherGroup, otherIndex) => {
      if (index === otherIndex || processed.has(otherIndex)) return;

      const distance = getDistance(
        { latitude: group.lat, longitude: group.lng },
        { latitude: otherGroup.lat, longitude: otherGroup.lng }
      );

      if (distance <= distanceThresholdMeters) {
        cluster.clusterSize++;
        cluster.allItems.push(...otherGroup.items);
        processed.add(otherIndex);
      }
    });

    processed.add(index);
    clusters.push(cluster);
  });

  return clusters;
};

// Returns all coordinate objects for an item (multi-location aware)
const getItemLocations = (item) => {
  if (item.locations?.length > 0) {
    const coords = item.locations.map(l => l.coordinates).filter(c => c?.lat && c?.lng);
    if (coords.length > 0) return coords;
  }
  if (item.coordinates?.lat && item.coordinates?.lng) return [item.coordinates];
  // Fall back to shop-level master addresses (item inherits shop locations)
  if (item.shopMasterAddresses?.length > 0) {
    return item.shopMasterAddresses
      .map(a => a.coordinates)
      .filter(c => c?.lat && c?.lng);
  }
  return [];
};

const filterItemsByRadius = (items, userLocation, radiusInMiles) => {
  if (!userLocation) return items;
  const radiusInMeters = radiusInMiles * 1609.34;
  return items.filter(item => {
    const locs = getItemLocations(item);
    if (locs.length === 0) return false;
    return locs.some(coords => {
      const distance = getDistance(
        { latitude: userLocation.latitude, longitude: userLocation.longitude },
        { latitude: coords.lat, longitude: coords.lng }
      );
      return distance <= radiusInMeters;
    });
  });
};

// Builds a FeatureCollection of the 35-mile reference rings plus the outer
// search-radius ring, in GeoJSON (lng/lat) order, for the Source/Layer below.
const buildRadiusRingsGeoJSON = (lat, lng, radiusInMiles) => {
  const toRingFeature = (radiusMiles, kind) => ({
    type: 'Feature',
    properties: { kind },
    geometry: turfCircle([lng, lat], radiusMiles, { steps: 64, units: 'miles' }).geometry
  });

  const referenceRingMiles = Array.from(
    { length: Math.floor(radiusInMiles / 35) },
    (_, i) => (i + 1) * 35
  );

  return {
    type: 'FeatureCollection',
    features: [
      ...referenceRingMiles.map(miles => toRingFeature(miles, 'ring')),
      toRingFeature(radiusInMiles, 'outer')
    ]
  };
};

// ============= MARKER SIZING =============

// Pixel size bounds a chest marker is allowed to scale between — kept tight
// so even the most zoomed-in view never reads as oversized.
const CHEST_SIZE_MIN = 12;  // zoomed all the way out
const CHEST_SIZE_MAX = 46;  // zoomed all the way in (street level)
const CHEST_ZOOM_MIN = 3;   // at/below this zoom, size is pinned to MIN
const CHEST_ZOOM_MAX = 18;  // at/above this zoom, size is pinned to MAX

// Returns pixel dimensions for a chest based on current zoom + cluster count.
// Zoomed OUT (viewing a whole city/region) packs a lot of real-world distance
// into few screen pixels, so markers stay small and out of the way instead of
// visually swallowing the map; zoomed IN gives each shop room to read clearly
// as a landmark. Size interpolates smoothly across the whole zoom range
// instead of jumping between fixed steps.
const getChestConfigForZoom = (zoom, clusterSize) => {
  const t = Math.min(1, Math.max(0, (zoom - CHEST_ZOOM_MIN) / (CHEST_ZOOM_MAX - CHEST_ZOOM_MIN)));
  let size = CHEST_SIZE_MIN + t * (CHEST_SIZE_MAX - CHEST_SIZE_MIN);

  // Modest scale-up for large clusters, still capped so it can never balloon
  // past a "landmark" footprint.
  if (clusterSize > 15)     size *= 1.15;
  else if (clusterSize > 5) size *= 1.08;
  size = Math.round(Math.min(size, CHEST_SIZE_MAX * 1.15));

  const badge = Math.max(9, Math.round(size * 0.5));
  return { width: size, height: size, badge };
};

// Normalizes the various shapes `shopProfilePicture` shows up in across the
// codebase (plain URL string, or an object with .preview/.url from an
// in-progress upload) down to a usable <img> src, or null.
const getShopAvatarUrl = (item) => {
  const pic = item?.shopProfilePicture;
  if (!pic) return null;
  if (typeof pic === 'string') return pic;
  return pic.preview || pic.url || null;
};

// Below this zoom, pins are too small/dense for a name label to read cleanly
const LABEL_MIN_ZOOM = 10;

// ============= MARKER CONTENT (rendered inside MapLibre <Marker>) =============

const ChestMarkerWrap = styled.div`
  position: relative;
  filter: drop-shadow(2px 4px 8px rgba(0, 0, 0, 0.6));
  cursor: pointer;
`;

const ChestBadge = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  background: ${props => props.$color};
  color: #000;
  border-radius: 50%;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => Math.max(8, Math.floor(props.$size * 0.5))}px;
  font-weight: bold;
  border: 2px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  z-index: 10;
  transition: width 0.18s ease, height 0.18s ease;
`;

const ChestLabel = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  margin-top: 5px;
  transform: translateX(-50%);
  white-space: nowrap;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  background: rgba(15, 10, 20, 0.72);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  /* Scales with the marker's current pfp size, so the label grows/shrinks
     in the same proportion as the chest/avatar icon as zoom changes. */
  font-size: ${props => Math.max(8, Math.round(props.$markerSize * 0.28))}px;
  font-weight: 600;
  letter-spacing: 0.01em;
  font-family: Inter, sans-serif;
  padding: ${props => Math.max(2, Math.round(props.$markerSize * 0.065))}px ${props => Math.max(6, Math.round(props.$markerSize * 0.17))}px;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
  pointer-events: none;
  animation: chestLabelIn 0.15s ease-out;
  transition: font-size 0.18s ease, padding 0.18s ease;

  @keyframes chestLabelIn {
    from { opacity: 0; transform: translateX(-50%) translateY(-2px); }
    to   { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
`;

const ChestMarkerContent = ({ count, type, width, height, avatarUrl, shopName, badge, zoom }) => {
  const [imgError, setImgError] = useState(false);
  const showLabel = zoom >= LABEL_MIN_ZOOM && !!shopName;
  const badgeColor = type === 'product' ? '#FFD700' :
                     type === 'service' ? '#C0C0C0' :
                     '#CD7F32';
  const showAvatar = avatarUrl && !imgError;

  return (
    <ChestMarkerWrap>
      {showAvatar ? (
        <img
          src={avatarUrl}
          alt="shop"
          onError={() => setImgError(true)}
          style={{
            width, height, objectFit: 'cover', display: 'block',
            borderRadius: '50%', border: '2px solid #fff', background: '#2a2a2a',
            transition: 'width 0.18s ease, height 0.18s ease'
          }}
        />
      ) : (
        <img
          src="/treasure-chest.png"
          alt="treasure chest"
          style={{
            width, height, objectFit: 'contain', display: 'block', background: 'transparent',
            transition: 'width 0.18s ease, height 0.18s ease'
          }}
        />
      )}
      <ChestBadge $color={badgeColor} $size={badge}>{count}</ChestBadge>
      {showLabel && <ChestLabel $markerSize={width}>{shopName}</ChestLabel>}
    </ChestMarkerWrap>
  );
};

const ShipPingRing = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 56px;
  height: 56px;
  border: 2px solid ${props => props.$color}cc;
  border-radius: 50%;
  animation: shipPing 2.5s cubic-bezier(0, 0, 0.2, 1) infinite;
  pointer-events: none;

  @keyframes shipPing {
    0%        { transform: translate(-50%, -50%) scale(0.7); opacity: 1; }
    75%, 100% { transform: translate(-50%, -50%) scale(1.8); opacity: 0; }
  }
`;

const UserShipMarkerContent = ({ accentColor = '#c084fc' }) => (
  <div style={{ position: 'relative', filter: 'drop-shadow(2px 2px 8px rgba(0,0,0,0.7))' }}>
    <img src="/pirate-ship.png" alt="pirate ship" style={{ width: 44, height: 44, objectFit: 'contain', display: 'block', background: 'transparent' }} />
    <ShipPingRing $color={accentColor} />
  </div>
);

// ============= STYLED COMPONENTS =============

const MapOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(3px);
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const SelectionMarker = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1001;
  pointer-events: none;

  .crosshair {
    width: 40px;
    height: 40px;
    position: relative;

    &::before,
    &::after {
      content: '';
      position: absolute;
      background: ${props => props.theme?.colors?.accent || '#8b5a2b'};
    }

    &::before {
      width: 2px;
      height: 100%;
      left: 50%;
      transform: translateX(-50%);
    }

    &::after {
      width: 100%;
      height: 2px;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  .pin-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -100%);
    color: ${props => props.theme?.colors?.accent || '#8b5a2b'};
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.6));
  }
`;

const MapContainer_Styled = styled.div`
  position: absolute;
  top: 3%;
  left: 3%;
  right: 3%;
  bottom: 3%;
  background: ${props => props.theme?.colors?.accent || '#c084fc'};
  border-radius: 10px;
  overflow: hidden;
  border: 3px solid ${props => props.theme?.colors?.accent || '#c084fc'};
  box-shadow:
    inset 0 0 0 2px ${props => `${props.theme?.colors?.accent || '#c084fc'}60`},
    0 18px 60px rgba(0,0,0,0.7),
    0 4px 12px rgba(0,0,0,0.35);
  animation: gmapsOpen 0.28s cubic-bezier(0.22,1,0.36,1);

  @keyframes gmapsOpen {
    from { transform: scale(0.95); opacity: 0; }
    to   { transform: scale(1);    opacity: 1; }
  }

  @media (max-width: 768px) {
    top: 0; left: 0; right: 0; bottom: 0;
    border-radius: 0;
    border: none;
    box-shadow: none;
  }

  /* Subtle vignette overlay */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 900;
    background: radial-gradient(ellipse at 50% 50%, transparent 55%, rgba(0,0,0,0.35) 100%);
    opacity: 0.6;
  }

  .maplibregl-map {
    height: 100%;
    width: 100%;
    background: ${props => props.theme?.colors?.background || '#0f0a14'};
    font-family: ${props => props.theme?.fonts?.body || 'Inter, sans-serif'};
  }

  /* Attribution stays present (required by the tile data license) but tucked
     directly behind the GPS button — same corner offset, lower z-index, so
     the GPS button fully covers it instead of it floating visibly on the map. */
  .maplibregl-ctrl-bottom-right {
    bottom: 0.75rem;
    right: 0.75rem;
    z-index: 500;

    @media (max-width: 768px) {
      right: calc(0.75rem + env(safe-area-inset-right));
      bottom: calc(0.75rem + env(safe-area-inset-bottom));
    }
  }

  .maplibregl-ctrl-attrib.maplibregl-compact {
    margin: 0;
  }
`;

const MapHeader = styled.div`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  right: 0.75rem;
  z-index: 1000;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${props => props.theme?.colors?.backdropColor || 'rgba(15, 10, 20, 0.93)'};
  backdrop-filter: blur(16px);
  padding: 0.6rem 0.85rem;
  border-radius: 8px;
  box-shadow: 0 3px 14px rgba(0,0,0,0.5), inset 0 1px 0 ${props => `${props.theme?.colors?.accent || '#c084fc'}22`};
  border: 1px solid ${props => `${props.theme?.colors?.accent || '#c084fc'}60`};

  @media (max-width: 768px) {
    top: calc(0.5rem + env(safe-area-inset-top));
    left: calc(0.5rem + env(safe-area-inset-left));
    right: calc(0.5rem + env(safe-area-inset-right));
    padding: 0.5rem 0.75rem;
  }
`;

const MapTitle = styled.h2`
  margin: 0;
  font-family: ${props => props.theme?.fonts?.heading || 'Georgia, serif'};
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: ${props => props.theme?.colors?.text || '#f3e8ff'};
  text-shadow: 1px 1px 4px rgba(0,0,0,0.7);

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const MapControls = styled.div`
  display: flex;
  gap: 0.45rem;
  align-items: center;
`;

/* ---- Compact search-radius control, lives in the header next to Close ---- */
const HeaderRadiusControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  background: ${props => `${props.theme?.colors?.text || '#f3e8ff'}10`};
  border: 1px solid ${props => `${props.theme?.colors?.text || '#f3e8ff'}40`};
  border-radius: 999px;
  padding: 0.22rem 0.3rem;

  @media (max-width: 768px) {
    padding: 0.18rem 0.25rem;
    gap: 0.22rem;
  }
`;

const HeaderRadiusValue = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  color: ${props => props.theme?.colors?.accent || '#c084fc'};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  padding: 0 0.1rem;

  @media (max-width: 768px) {
    font-size: 0.64rem;
  }
`;

const HeaderRadiusButton = styled.button`
  background: ${props => `${props.theme?.colors?.accent || '#c084fc'}15`};
  border: 1px solid ${props => `${props.theme?.colors?.accent || '#c084fc'}50`};
  color: ${props => props.theme?.colors?.text || '#f3e8ff'};
  width: 22px;
  height: 22px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  font-weight: 300;
  line-height: 1;
  flex-shrink: 0;
  transition: background 0.12s, border-color 0.12s;

  &:hover:not(:disabled) {
    background: ${props => `${props.theme?.colors?.accent || '#c084fc'}30`};
    border-color: ${props => props.theme?.colors?.accent || '#c084fc'};
    color: ${props => props.theme?.colors?.accent || '#c084fc'};
  }

  &:active:not(:disabled) {
    background: ${props => `${props.theme?.colors?.accent || '#c084fc'}45`};
  }

  &:disabled {
    opacity: 0.28;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 19px;
    height: 19px;
    font-size: 0.82rem;
  }
`;

/* ---- GPS location toggle, moved to bottom-right where radius used to be ---- */
const GPSLocationButton = styled.button`
  position: absolute;
  bottom: 0.75rem;
  right: 0.75rem;
  z-index: 1000;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  background: ${props => props.theme?.colors?.backdropColor || 'rgba(15, 10, 20, 0.93)'};
  border: 1px solid ${props => `${props.theme?.colors?.accent || '#c084fc'}55`};
  color: ${props => props.theme?.colors?.text || '#f3e8ff'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(14px);
  box-shadow: 0 4px 16px rgba(0,0,0,0.45), inset 0 1px 0 ${props => `${props.theme?.colors?.accent || '#c084fc'}15`};
  transition: all 0.15s;

  &:hover {
    background: ${props => `${props.theme?.colors?.accent || '#c084fc'}25`};
    border-color: ${props => props.theme?.colors?.accent || '#c084fc'};
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 768px) {
    right: calc(0.75rem + env(safe-area-inset-right));
    bottom: calc(0.75rem + env(safe-area-inset-bottom));
    width: 2.5rem;
    height: 2.5rem;
    svg { width: 18px; height: 18px; }
  }
`;

const MapButton = styled.button`
  background: ${props => props.active
    ? `${props.theme?.colors?.accent || '#c084fc'}30`
    : `${props.theme?.colors?.text || '#f3e8ff'}10`};
  border: 1px solid ${props => props.active
    ? `${props.theme?.colors?.accent || '#c084fc'}cc`
    : `${props.theme?.colors?.text || '#f3e8ff'}40`};
  color: ${props => props.active
    ? (props.theme?.colors?.accent || '#c084fc')
    : (props.theme?.colors?.text || '#f3e8ff')};
  padding: 0.42rem;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: ${props => `${props.theme?.colors?.accent || '#c084fc'}25`};
    border-color: ${props => props.theme?.colors?.accent || '#c084fc'};
    color: ${props => props.theme?.colors?.accent || '#c084fc'};
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }

  @media (max-width: 768px) {
    padding: 0.38rem;
    svg { width: 16px; height: 16px; }
  }
`;

/* ---- Zoom controls (right side, above the GPS button) ---- */
const CustomZoomControls = styled.div`
  position: absolute;
  right: 0.75rem;
  bottom: 4rem;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 0;
  background: ${props => props.theme?.colors?.backdropColor || 'rgba(15, 10, 20, 0.93)'};
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.45), inset 0 1px 0 ${props => `${props.theme?.colors?.accent || '#c084fc'}15`};
  border: 1px solid ${props => `${props.theme?.colors?.accent || '#c084fc'}55`};

  @media (max-width: 768px) {
    right: calc(0.75rem + env(safe-area-inset-right));
    bottom: calc(3.75rem + env(safe-area-inset-bottom));
  }
`;

const ZoomButton = styled.button`
  background: transparent;
  border: none;
  border-bottom: 1px solid ${props => `${props.theme?.colors?.accent || '#c084fc'}40`};
  color: ${props => props.theme?.colors?.text || '#f3e8ff'};
  width: 36px;
  height: 36px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  font-weight: 300;
  line-height: 1;
  transition: background 0.1s, color 0.1s;

  &:last-child { border-bottom: none; }

  &:hover:not(:disabled) {
    background: ${props => `${props.theme?.colors?.accent || '#c084fc'}20`};
    color: ${props => props.theme?.colors?.accent || '#c084fc'};
  }

  &:active:not(:disabled) {
    background: ${props => `${props.theme?.colors?.accent || '#c084fc'}35`};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const ZoomLevelLabel = styled.div`
  width: 36px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${props => `${props.theme?.colors?.text || '#f3e8ff'}cc`};
  border-bottom: 1px solid ${props => `${props.theme?.colors?.accent || '#c084fc'}40`};
  pointer-events: none;
`;

const ZoomOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 12000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ZoomContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  height: 80vh;
  max-height: 700px;
  background: ${props => props.theme?.colors?.background || '#000000'};
  border-radius: 16px;
  overflow: hidden;
  border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    max-width: 420px;
    border-radius: 12px;
    height: calc(100vh - 2rem);
    max-height: 650px;
  }

  @media (max-width: 480px) {
    max-width: 95%;
    border-radius: 10px;
    height: calc(100vh - 1rem);
    max-height: 600px;
  }
`;

const ImageCarousel = styled.div`
  position: relative;
  flex: 7;
  overflow: hidden;

  .image-track {
    display: flex;
    height: 100%;
    width: 100%;
  }

  .image-slide {
    min-width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .no-image {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;
      opacity: 0.3;
      color: ${props => props.theme?.colors?.text || '#FFFFFF'};

      p {
        margin: 0;
        font-size: 0.85rem;
      }
    }
  }

  .carousel-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: #fff;
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #5f6368;
    cursor: pointer;
    opacity: 0.9;
    transition: all 0.15s;
    z-index: 2;
    box-shadow: 0 1px 4px rgba(0,0,0,0.3);

    &:hover {
      opacity: 1;
      transform: translateY(-50%) scale(1.1);
    }

    @media (max-width: 480px) {
      width: 32px;
      height: 32px;
      svg { width: 16px; height: 16px; }
    }
  }

  .close-overlay-button {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    background: #fff;
    border: none;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #5f6368;
    cursor: pointer;
    z-index: 3;
    opacity: 0.9;
    box-shadow: 0 1px 4px rgba(0,0,0,0.25);
    transition: all 0.15s;

    &:hover {
      opacity: 1;
      background: ${props => props.theme?.colors?.accent || '#800000'};
      transform: scale(1.1);
    }

    &:active { transform: scale(0.95); }

    @media (max-width: 480px) {
      width: 32px;
      height: 32px;
      svg { width: 16px; height: 16px; }
    }
  }
`;

const ZoomContent = styled.div`
  flex: 3;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: ${props => props.theme?.colors?.background || '#000000'};
  position: relative;

  ${props => props.$isMobile && `display: none;`}

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${props => `${props.theme?.colors?.accent || '#800000'}60`};
    border-radius: 2px;
    &:hover { background: ${props => props.theme?.colors?.accent || '#800000'}; }
  }

  @media (max-width: 480px) { padding: 0.875rem; }
`;

/* ---- Bottom-left location badge ---- */
const LocationModeIndicator = styled.div`
  position: absolute;
  bottom: 0.75rem;
  left: 0.75rem;
  background: ${props => props.theme?.colors?.backdropColor || 'rgba(15, 10, 20, 0.93)'};
  backdrop-filter: blur(10px);
  border: 1px solid ${props => `${props.theme?.colors?.accent || '#c084fc'}50`};
  padding: 0.4rem 0.65rem;
  border-radius: 6px;
  color: ${props => `${props.theme?.colors?.text || '#f3e8ff'}cc`};
  font-size: 0.72rem;
  font-family: ${props => props.theme?.fonts?.body || 'Inter, sans-serif'};
  letter-spacing: 0.03em;
  box-shadow: 0 2px 10px rgba(0,0,0,0.35);
  display: flex;
  align-items: center;
  gap: 0.45rem;
  z-index: 1000;

  @media (max-width: 768px) {
    bottom: calc(0.5rem + env(safe-area-inset-bottom));
    left: calc(0.5rem + env(safe-area-inset-left));
    font-size: 0.7rem;
  }

  .status-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${props =>
      props.mode === 'gps'       ? '#4CAF50' :
      props.mode === 'acquiring' ? (props.theme?.colors?.accent || '#c084fc') :
      props.mode === 'selection' ? '#4CAF50' :
      `${props.theme?.colors?.accent || '#c084fc'}80`
    };
    animation: ${props => props.mode === 'acquiring' ? 'dotPulse 2s infinite' : 'none'};
  }

  @keyframes dotPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
`;

/* ---- Confirm location button (selection mode) ---- */
const SelectLocationButton = styled.button`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  background: ${props => props.theme?.colors?.accentGradient || props.theme?.colors?.accent || '#c084fc'};
  color: #fff;
  border: 1px solid ${props => `${props.theme?.colors?.accent || '#c084fc'}80`};
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.875rem;
  font-family: ${props => props.theme?.fonts?.body || 'Inter, sans-serif'};
  letter-spacing: 0.04em;
  cursor: pointer;
  z-index: 1001;
  box-shadow: 0 2px 10px rgba(0,0,0,0.4);
  transition: all 0.15s;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    font-size: 1rem;
    bottom: calc(2rem + env(safe-area-inset-bottom));
    right: calc(0.75rem + env(safe-area-inset-right));
    min-height: 48px;
  }

  &:hover {
    filter: brightness(1.12);
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(0,0,0,0.5);
  }

  &:active { transform: translateY(0); }
`;

/* ---- Selection mode cancel button ---- */
const CancelLocationButton = styled.button`
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  background: ${props => props.theme?.colors?.backdropColor || 'rgba(15, 10, 20, 0.88)'};
  color: ${props => `${props.theme?.colors?.text || '#f3e8ff'}bb`};
  border: 1px solid ${props => `${props.theme?.colors?.text || '#f3e8ff'}30`};
  padding: 0.75rem 1.25rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.875rem;
  font-family: ${props => props.theme?.fonts?.body || 'Inter, sans-serif'};
  letter-spacing: 0.04em;
  cursor: pointer;
  z-index: 1001;
  box-shadow: 0 2px 10px rgba(0,0,0,0.3);
  transition: all 0.15s;
  backdrop-filter: blur(8px);

  @media (max-width: 768px) {
    padding: 1rem 1.25rem;
    font-size: 1rem;
    bottom: calc(2rem + env(safe-area-inset-bottom));
    left: calc(0.75rem + env(safe-area-inset-left));
    min-height: 48px;
  }

  &:hover {
    background: ${props => `${props.theme?.colors?.accent || '#c084fc'}20`};
    color: ${props => props.theme?.colors?.text || '#f3e8ff'};
    border-color: ${props => `${props.theme?.colors?.accent || '#c084fc'}60`};
  }

  &:active { transform: translateY(0); }
`;

/* ---- Selection mode address search bar ---- */
const SelectionAddressBar = styled.div`
  position: absolute;
  top: 0.75rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: ${props => props.theme?.colors?.backdropColor || 'rgba(15, 10, 20, 0.93)'};
  backdrop-filter: blur(14px);
  border: 1px solid ${props => `${props.theme?.colors?.accent || '#c084fc'}60`};
  border-radius: 8px;
  padding: 0.35rem 0.5rem;
  box-shadow: 0 4px 16px rgba(0,0,0,0.45);
  width: min(340px, calc(100% - 5.5rem));

  @media (max-width: 768px) {
    top: calc(0.75rem + env(safe-area-inset-top));
  }

  input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: ${props => props.theme?.colors?.text || '#f3e8ff'};
    font-size: 0.85rem;
    font-family: ${props => props.theme?.fonts?.body || 'Inter, sans-serif'};
    min-width: 0;
    &::placeholder { color: ${props => `${props.theme?.colors?.text || '#f3e8ff'}55`}; }
  }

  button {
    background: ${props => props.theme?.colors?.accentGradient || props.theme?.colors?.accent || '#c084fc'};
    border: 1px solid ${props => `${props.theme?.colors?.accent || '#c084fc'}50`};
    border-radius: 5px;
    color: #fff;
    padding: 0.25rem 0.6rem;
    font-size: 0.78rem;
    font-family: ${props => props.theme?.fonts?.body || 'Inter, sans-serif'};
    cursor: pointer;
    white-space: nowrap;
    transition: filter 0.15s;
    &:hover { filter: brightness(1.15); }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }
`;

/* ---- Selection mode GPS / navigation button ---- */
const SelectionNavButton = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 1001;
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 8px;
  background: ${props => props.theme?.colors?.backdropColor || 'rgba(15, 10, 20, 0.93)'};
  backdrop-filter: blur(14px);
  border: 1px solid ${props => `${props.theme?.colors?.accent || '#c084fc'}60`};
  box-shadow: 0 4px 16px rgba(0,0,0,0.45);
  color: ${props => props.theme?.colors?.text || '#f3e8ff'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  &:hover {
    background: ${props => `${props.theme?.colors?.accent || '#c084fc'}30`};
    color: ${props => props.theme?.colors?.accent || '#c084fc'};
  }

  @media (max-width: 768px) {
    top: calc(0.75rem + env(safe-area-inset-top));
    right: calc(0.75rem + env(safe-area-inset-right));
  }
`;

// ============= MAIN COMPONENT =============

const TreasureMap = ({
  isOpen,
  onClose,
  items = [],
  services = [],
  userLocation,
  theme,
  onItemClick,
  isAuthenticated,
  onRequestLocation,
  onLocationUpdate,
  selectionMode = false,
  onLocationSelect
}) => {

  const [locationMode, setLocationMode] = useState('ip'); // 'ip' | 'acquiring' | 'gps'
  const [radiusInMiles, setRadiusInMiles] = useState(100);
  const [maxRadius] = useState(isAuthenticated ? 1000 : 300);
  const [minRadius] = useState(0.2);
  // Incrementing this triggers the fly-to effect below to fly to the target
  const [flyTrigger, setFlyTrigger] = useState(0);
  const [flyTarget, setFlyTarget] = useState(null);
  const [flyZoom, setFlyZoom] = useState(12);
  const [currentZoom, setCurrentZoom] = useState(selectionMode ? 15 : 12);
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSearching, setAddressSearching] = useState(false);
  const [nightMode, setNightMode] = useState(true);
  const [nightStyle, setNightStyle] = useState(null);
  const [dayStyle, setDayStyle] = useState(null);

  const mapRef = useRef(null);
  const hasFlownOnOpenRef = useRef(false);
  const navigate = useNavigate();

  // Fetch + recolor both styles once per mount, so toggling night mode on/off
  // repeatedly doesn't re-fetch — each falls back to its stock style URL
  // until its recolored version resolves.
  useEffect(() => {
    let cancelled = false;
    fetch(MAP_STYLE_URL_NIGHT)
      .then(res => res.json())
      .then(json => { if (!cancelled) setNightStyle(recolorNightHighways(json)); })
      .catch(() => {});
    fetch(MAP_STYLE_URL)
      .then(res => res.json())
      .then(json => { if (!cancelled) setDayStyle(remixDayColors(json)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // ============= FLY HELPERS =============

  const flyToLocation = useCallback((location, zoom) => {
    setFlyTarget(location);
    setFlyZoom(zoom || 13);
    setFlyTrigger(prev => prev + 1);
  }, []);

  // Actually performs the fly, whenever flyTrigger changes (replaces the old
  // react-leaflet MapFlyController sub-component — mapRef is already a
  // plain ref here, so no map-context component is needed to reach it).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !flyTarget) return;
    try {
      map.flyTo({ center: [flyTarget.longitude, flyTarget.latitude], zoom: flyZoom, duration: 1200 });
    } catch (e) {
      console.error('Treasure map flyTo error:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flyTrigger]);

  // ============= FLY TO USER LOCATION ON OPEN (selection mode) =============

  useEffect(() => {
    if (!isOpen || !selectionMode) {
      hasFlownOnOpenRef.current = false;
      return;
    }
    if (!userLocation || hasFlownOnOpenRef.current) return;
    hasFlownOnOpenRef.current = true;
    flyToLocation(userLocation, 13);
  }, [isOpen, selectionMode, userLocation, flyToLocation]);

  // ============= ADDRESS SEARCH (selection mode) =============

  const handleAddressSearch = useCallback(async () => {
    if (!addressQuery.trim()) return;
    setAddressSearching(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(addressQuery.trim())}&format=json&limit=1`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
      const data = await res.json();
      if (data && data[0]) {
        flyToLocation({ latitude: parseFloat(data[0].lat), longitude: parseFloat(data[0].lon) }, 16);
      }
    } catch (_) {}
    setAddressSearching(false);
  }, [addressQuery, flyToLocation]);

  // ============= ZOOM =============

  const handleZoomIn = useCallback(() => {
    if (mapRef.current) mapRef.current.zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    if (mapRef.current) mapRef.current.zoomOut();
  }, []);

  // ============= MEMOIZED VALUES =============

  const mapCenter = useMemo(() => {
    if (!userLocation) return null;
    return { latitude: userLocation.latitude, longitude: userLocation.longitude };
  }, [userLocation?.latitude, userLocation?.longitude]);

  const clusteredGroups = useMemo(() => {
    if (!mapCenter) return [];

    const allItems = [
      ...items.map(i => ({ ...i, type: 'product' })),
      ...services.map(s => ({ ...s, type: 'service' }))
    ];

    const itemsInRadius = filterItemsByRadius(allItems, mapCenter, radiusInMiles);

    const groups = {};
    itemsInRadius.forEach(item => {
      const locs = getItemLocations(item);
      if (locs.length === 0) return;
      locs.forEach(coords => {
        const lat = Math.round(coords.lat * 1000) / 1000;
        const lng = Math.round(coords.lng * 1000) / 1000;
        const key = `${lat},${lng}`;
        if (!groups[key]) {
          groups[key] = { lat: coords.lat, lng: coords.lng, items: [], products: [], services: [] };
        }
        // Avoid adding the same item twice to the same cluster bucket
        const itemId = item.id || item.tempId;
        const alreadyIn = groups[key].items.some(i => (i.id || i.tempId) === itemId);
        if (!alreadyIn) {
          groups[key].items.push(item);
          if (item.type === 'product') groups[key].products.push(item);
          else groups[key].services.push(item);
        }
      });
    });

    return clusterNearbyItems(Object.values(groups), 1609.34);
  }, [items, services, radiusInMiles, mapCenter]);

  const radiusRingsGeoJSON = useMemo(() => {
    if (!mapCenter || selectionMode) return null;
    return buildRadiusRingsGeoJSON(mapCenter.latitude, mapCenter.longitude, radiusInMiles);
  }, [mapCenter, radiusInMiles, selectionMode]);

  // ============= RADIUS HANDLERS =============

  const handleRadiusIncrease = useCallback(() => {
    setRadiusInMiles(prev => {
      if (prev < 5)  return Math.min(prev + 0.5, maxRadius);
      if (prev < 20) return Math.min(prev + 1,   maxRadius);
      if (prev < 50) return Math.min(prev + 5,   maxRadius);
      return Math.min(prev + 10, maxRadius);
    });
  }, [maxRadius]);

  const handleRadiusDecrease = useCallback(() => {
    setRadiusInMiles(prev => {
      if (prev <= 5)  return Math.max(prev - 0.5, minRadius);
      if (prev <= 20) return Math.max(prev - 1,   minRadius);
      if (prev <= 50) return Math.max(prev - 5,   minRadius);
      return Math.max(prev - 10, minRadius);
    });
  }, [minRadius]);

  // ============= CALLBACKS =============

  const handleClusterClick = useCallback((cluster) => {
    if (cluster.allItems.length === 0) return;
    // Same "primary" item the marker's pfp is drawn from — clicking the pfp
    // should take you to the shop it's actually showing.
    const primaryItem = cluster.allItems.find(i => getShopAvatarUrl(i)) || cluster.allItems[0];
    const shopId = primaryItem?.shopId;
    onClose();
    if (shopId) navigate(`/shop/${shopId}/view?tab=shop`);
  }, [onClose, navigate]);

  const handleSelectLocation = useCallback(() => {
    if (mapRef.current && onLocationSelect) {
      const center = mapRef.current.getCenter();
      onLocationSelect({ lat: center.lat, lng: center.lng });
      onClose();
    }
  }, [onLocationSelect, onClose]);

  // One-time GPS read — no continuous watchPosition tracking. Reused by both
  // the nav icon click and the auto-read-on-open effect below. `shouldFly`
  // is false for the silent auto-read on open, so the map stays put instead
  // of zooming/panning to the user without them asking for it.
  const acquireGPSLocation = useCallback(async (shouldFly = true) => {
    if (!navigator.geolocation) {
      setLocationMode('ip');
      return;
    }
    setLocationMode('acquiring');
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const gpsLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      };

      setLocationMode('gps');
      if (shouldFly) flyToLocation(gpsLocation, 14);
      if (onRequestLocation) onRequestLocation(gpsLocation);
    } catch (error) {
      console.error('❌ GPS request failed:', error);
      if (error.code === 1) alert('Location permission denied.');
      else if (error.code === 2) alert('Unable to get your location.');
      else if (error.code === 3) alert('Location request timed out.');
      setLocationMode('ip');
    }
  }, [onRequestLocation, flyToLocation]);

  const handleLocationModeToggle = useCallback(() => {
    // Already have a GPS fix → revert to IP
    if (locationMode === 'gps') {
      setLocationMode('ip');
      if (onRequestLocation) onRequestLocation({ revertToIP: true });
      if (userLocation) flyToLocation(userLocation, 12);
      return;
    }
    acquireGPSLocation();
  }, [locationMode, onRequestLocation, userLocation, flyToLocation, acquireGPSLocation]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  // ============= EFFECTS =============

  // Auto-read GPS once when the map opens, but only if the browser already
  // has geolocation permission granted from a previous visit — otherwise
  // wait for the user to tap the nav icon (which triggers the permission
  // prompt themselves).
  useEffect(() => {
    if (!isOpen || selectionMode) return;
    if (locationMode !== 'ip') return;
    if (!navigator.permissions?.query) return;

    let cancelled = false;
    navigator.permissions.query({ name: 'geolocation' })
      .then(result => {
        if (!cancelled && result.state === 'granted') {
          acquireGPSLocation(false);
        }
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [isOpen, selectionMode]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen || !userLocation || !mapCenter) return null;

  const radiusDisplayMiles = radiusInMiles < 1
    ? radiusInMiles.toFixed(1)
    : Math.round(radiusInMiles);

  return (
    <MapOverlay onClick={handleOverlayClick}>
      <MapContainer_Styled theme={theme} onClick={(e) => e.stopPropagation()}>

        {/* ---- Header ---- */}
        <MapHeader theme={theme}>
           <MapTitle theme={theme}>
            {selectionMode ? '' : '🗺️ Treasure Map'}
          </MapTitle>
          <MapControls>
            {!selectionMode && (
              <HeaderRadiusControl
                theme={theme}
                title={isAuthenticated
                  ? `Search radius: 0.2–${maxRadius} mi`
                  : `Search radius: 0.2–${maxRadius} mi (login for more)`}
              >
                <HeaderRadiusButton theme={theme} onClick={handleRadiusDecrease} disabled={radiusInMiles <= minRadius} title="Decrease radius">
                  −
                </HeaderRadiusButton>
                <HeaderRadiusValue theme={theme}>{radiusDisplayMiles} mi</HeaderRadiusValue>
                <HeaderRadiusButton theme={theme} onClick={handleRadiusIncrease} disabled={radiusInMiles >= maxRadius} title="Increase radius">
                  +
                </HeaderRadiusButton>
              </HeaderRadiusControl>
            )}
            <MapButton
              theme={theme}
              onClick={() => setNightMode(n => !n)}
              active={nightMode}
              title={nightMode ? 'Switch to day mode' : 'Switch to night mode'}
            >
              {nightMode ? <Sun size={18} /> : <Moon size={18} />}
            </MapButton>
            <MapButton theme={theme} onClick={onClose} title="Close Map">
              <X size={18} />
            </MapButton>
          </MapControls>
        </MapHeader>

        {/* ---- MapLibre Map (OpenFreeMap vector tiles) ---- */}
        <MapGL
          ref={mapRef}
          mapStyle={nightMode ? (nightStyle || MAP_STYLE_URL_NIGHT) : (dayStyle || MAP_STYLE_URL)}
          initialViewState={{
            longitude: mapCenter.longitude,
            latitude: mapCenter.latitude,
            zoom: selectionMode ? 15 : 12
          }}
          style={{ height: '100%', width: '100%' }}
          dragRotate={false}
          touchPitch={false}
          pitchWithRotate={false}
          maxPitch={0}
          attributionControl={{ compact: true }}
          onZoom={(e) => setCurrentZoom(e.viewState.zoom)}
          onLoad={(e) => {
            // `compact: true` only auto-collapses once the map has been moved —
            // force it closed immediately so it's tucked behind the GPS button
            // (see .maplibregl-ctrl-bottom-right below) from the first frame.
            const attribEl = e.target.getContainer()?.querySelector('.maplibregl-ctrl-attrib');
            if (attribEl) {
              attribEl.classList.remove('maplibregl-compact-show');
              attribEl.removeAttribute('open');
            }
          }}
        >
          {!selectionMode && radiusRingsGeoJSON && (
            <Source id="radius-rings" type="geojson" data={radiusRingsGeoJSON}>
              <Layer
                id="radius-ring-lines"
                type="line"
                filter={['==', ['get', 'kind'], 'ring']}
                paint={{
                  'line-color': theme?.colors?.accent || '#c084fc',
                  'line-width': 1,
                  'line-dasharray': [4, 8],
                  'line-opacity': 0.35
                }}
              />
              <Layer
                id="radius-outer-line"
                type="line"
                filter={['==', ['get', 'kind'], 'outer']}
                paint={{
                  'line-color': theme?.colors?.accent || '#c084fc',
                  'line-width': 2.5,
                  'line-dasharray': [8, 7],
                  'line-opacity': 0.75
                }}
              />
            </Source>
          )}

          {!selectionMode && (
            <MapMarker
              longitude={mapCenter.longitude}
              latitude={mapCenter.latitude}
              anchor="bottom"
              style={{ zIndex: 20 }}
            >
              <UserShipMarkerContent accentColor={theme?.colors?.accent} />
            </MapMarker>
          )}

          {!selectionMode && clusteredGroups.map((cluster, index) => {
            const hasProducts = cluster.allItems.some(i => i.type === 'product');
            const hasServices = cluster.allItems.some(i => i.type === 'service');
            const type = hasProducts && hasServices ? 'mixed'
                       : hasProducts ? 'product' : 'service';

            const { width, height, badge } = getChestConfigForZoom(currentZoom, cluster.allItems.length);

            // Use the pfp (and matching name) of the first shop in this cluster
            // that has one; clusters are almost always a single shop's items
            // grouped at one address, so this reads as "that shop's marker".
            const primaryItem = cluster.allItems.find(i => getShopAvatarUrl(i)) || cluster.allItems[0];
            const avatarUrl = getShopAvatarUrl(primaryItem);
            const shopName = primaryItem?.shopName || null;

            return (
              <MapMarker
                key={`cluster-${cluster.lat}-${cluster.lng}-${index}`}
                longitude={cluster.lng}
                latitude={cluster.lat}
                anchor="center"
                onClick={() => handleClusterClick(cluster)}
              >
                <ChestMarkerContent
                  count={cluster.allItems.length}
                  type={type}
                  width={width}
                  height={height}
                  badge={badge}
                  avatarUrl={avatarUrl}
                  shopName={shopName}
                  zoom={currentZoom}
                />
              </MapMarker>
            );
          })}
        </MapGL>

        {/* ---- Zoom buttons ---- */}
        {!selectionMode && (
          <CustomZoomControls theme={theme}>
            <ZoomButton theme={theme} onClick={handleZoomIn} title="Zoom in">+</ZoomButton>
            <ZoomLevelLabel theme={theme} title={`Zoom level ${Math.round(currentZoom)}`}>
              {Math.round(currentZoom)}
            </ZoomLevelLabel>
            <ZoomButton theme={theme} onClick={handleZoomOut} title="Zoom out">−</ZoomButton>
          </CustomZoomControls>
        )}

        {/* ---- Selection mode UI ---- */}
        {selectionMode && (
          <>
            <SelectionAddressBar theme={theme}>
              <input
                type="text"
                placeholder="Search address…"
                value={addressQuery}
                onChange={e => setAddressQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddressSearch()}
              />
              <button onClick={handleAddressSearch} disabled={addressSearching}>
                {addressSearching ? '…' : 'Go'}
              </button>
            </SelectionAddressBar>

            <SelectionNavButton
              theme={theme}
              title="Snap to my location"
              onClick={() => {
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (pos) => {
                      const loc = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
                      flyToLocation(loc, 16);
                      if (onRequestLocation) onRequestLocation(loc);
                    },
                    () => {
                      if (userLocation) flyToLocation(userLocation, 14);
                    },
                    { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
                  );
                } else if (userLocation) {
                  flyToLocation(userLocation, 14);
                }
              }}
            >
              <Navigation size={16} />
            </SelectionNavButton>

            <SelectionMarker theme={theme}>
              <div className="crosshair" />
              <MapPin size={32} className="pin-icon" />
            </SelectionMarker>

            <CancelLocationButton onClick={onClose} theme={theme}>
              Cancel
            </CancelLocationButton>

            <SelectLocationButton onClick={handleSelectLocation} theme={theme}>
              Confirm Location
            </SelectLocationButton>

            <LocationModeIndicator mode="selection" theme={theme}>
              <div className="status-dot" />
              <span>Drag map to select location</span>
            </LocationModeIndicator>
          </>
        )}

        {/* ---- GPS location toggle (bottom-right, where the radius control used to be) ---- */}
        {!selectionMode && (
          <GPSLocationButton
            theme={theme}
            onClick={handleLocationModeToggle}
            active={locationMode !== 'ip'}
            title={locationMode !== 'ip' ? 'Revert to IP location' : 'Use GPS location'}
          >
            <Navigation size={20} />
          </GPSLocationButton>
        )}

        {/* ---- Location mode badge (bottom-left) ---- */}
        {!selectionMode && (
          <LocationModeIndicator mode={locationMode} theme={theme}>
            <div className="status-dot" />
            <span>
              {locationMode === 'ip'        ? 'IP Location'    :
               locationMode === 'acquiring' ? 'Acquiring GPS…' :
               'GPS Location'}
            </span>
          </LocationModeIndicator>
        )}

      </MapContainer_Styled>
    </MapOverlay>
  );
};

export default TreasureMap;
